import { useMemo } from 'react';
import { parseDate, endOfMonth } from '@internationalized/date';
import type { CalendarViewMode } from '../../../types/calendar-view';

/**
 * Custom hook to determine the calendar view mode based on the selected date range
 * @param selectedValue - The selected date range value (either 'Any' or 'dateFrom,dateTo')
 * @returns The calendar view mode ('Weekly', 'Monthly', or 'Custom')
 */
export function useCalendarViewMode(selectedValue: string): CalendarViewMode {
  const mode = useMemo<CalendarViewMode>(() => {
    if (selectedValue === 'Any') return 'Custom';
    const [dateFrom, dateTo] = selectedValue.split(',');

    // Validate that both dates exist and are not empty
    if (!dateFrom || !dateTo || dateFrom.trim() === '' || dateTo.trim() === '') {
      return 'Custom';
    }

    // Check difference in length between dateFrom and dateTo in days
    const jsFromDate = new Date(dateFrom);
    const jsToDate = new Date(dateTo);
    const diffTime = Math.abs(jsToDate.getTime() - jsFromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

    if (diffDays === 7) return 'Weekly';

    // Check if it's a month: from date is 1st of month and to date is last day of month
    try {
      const fromDate = parseDate(dateFrom);
      const toDate = parseDate(dateTo);

      const isFirstOfMonth = fromDate.day === 1;
      const lastDayOfMonth = endOfMonth(fromDate);
      const isLastOfMonth = toDate.compare(lastDayOfMonth) === 0;

      if (isFirstOfMonth && isLastOfMonth) return 'Monthly';
    } catch {
      // Fall through to return 'Custom'
    }

    return 'Custom';
  }, [selectedValue]);

  return mode;
}
